import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FileSignature, Download } from 'lucide-react';
import { Panel, Field, FormRow, fmtDate } from './design';

// Agreement versions: each change to the agreement (services, prices, contribution, the yearly
// review) is a new version with what changed, sent to the participant, and their answer: agreed
// with the date, or not agreed with what they said. Each version prints exactly as it was offered.

const REASONS = [['services', 'Services changed'], ['prices', 'Prices changed'], ['contribution', 'Contribution changed'], ['review', 'Yearly review'], ['initial', 'First agreement'], ['other', 'Other']];
const STATUS = { draft: ['To send', 'badge-gray'], sent: ['Sent, waiting', 'badge-yellow'], agreed: ['Agreed', 'badge-green'], refused: ['Not agreed', 'badge-red'] };

export default function AgreementVersions({ plan, onSaved }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [answer, setAnswer] = useState(null);   // { id, status, date, note }
  const load = useCallback(() => {
    if (plan.id) api.get(`/aged-care/funding/${plan.id}/agreements`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);
  if (!plan.id) return null;
  const create = async () => {
    try { await api.post(`/aged-care/funding/${plan.id}/agreements`, form); toast.success('Agreement version made. Send it to them, then record their answer.'); setForm(null); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not make it'); }
  };
  const status = async (v, st, date, note) => {
    try { await api.post(`/aged-care/agreements/${v.id}/status`, { status: st, date, note }); toast.success('Recorded'); setAnswer(null); load(); if (onSaved && st === 'agreed') onSaved(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); }
  };
  const pdf = async (v) => {
    try {
      const r = await api.get(`/aged-care/agreements/${v.id}/pdf`, { responseType: 'blob' });
      const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `service-agreement-v${v.version}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch { toast.error('Could not build the agreement'); }
  };
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
  return (
    <Panel title="Agreement versions" icon={FileSignature} bodyClass="px-4 py-3 space-y-2 text-[13px]"
      actions={!form && <button type="button" className="btn-secondary btn-sm" onClick={() => setForm({ reason: rows.length ? 'services' : 'initial', changes: '' })}>New version</button>}>
      {!rows.length && !form && <p className="text-slate-500">No versions yet. A change to services, prices or contributions needs their agreement: make a version, send it, and record their answer.</p>}
      {form && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <FormRow cols={2}>
            <Field label="Why">{(id) => <select id={id} className="input-field" value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}>{REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}</Field>
            <Field label="Prices as at" hint="The schedule shows the prices in force on this date.">{(id) => <input id={id} type="date" className="input-field" value={form.asOf || ''} onChange={(e) => setForm((p) => ({ ...p, asOf: e.target.value }))} />}</Field>
          </FormRow>
          <Field label="What changed">{(id) => <textarea id={id} rows={2} className="input-field" value={form.changes} onChange={(e) => setForm((p) => ({ ...p, changes: e.target.value }))} />}</Field>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost btn-sm" onClick={() => setForm(null)}>Cancel</button>
            <button type="button" className="btn-primary btn-sm" onClick={create}>Make version</button>
          </div>
        </div>
      )}
      {rows.map((v) => {
        const [label, badge] = STATUS[v.status] || [v.status, 'badge-gray'];
        return (
          <div key={v.id} className="rounded-lg border border-slate-200 px-3 py-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-slate-900 font-medium">Version {v.version} <span className="font-normal text-slate-500">· {(REASONS.find(([k]) => k === v.reason) || [, v.reason])[1]} · {fmtDate(v.created_at)}</span></span>
              <span className="flex items-center gap-1.5">
                <span className={`badge ${badge}`}>{label}{v.agreed_at ? ` ${fmtDate(v.agreed_at)}` : v.refused_at ? ` ${fmtDate(v.refused_at)}` : ''}</span>
                <button type="button" className="btn-ghost btn-sm" onClick={() => pdf(v)}><Download size={13} /> PDF</button>
              </span>
            </div>
            {v.changes && <p className="text-slate-600">{v.changes}</p>}
            {v.response_note && <p className="text-slate-500">They said: {v.response_note}</p>}
            {['draft', 'sent'].includes(v.status) && (answer && answer.id === v.id ? (
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <input type="date" aria-label="Date" className="input-field !w-auto !py-1" value={answer.date} onChange={(e) => setAnswer((a) => ({ ...a, date: e.target.value }))} />
                {answer.status === 'refused' && <input aria-label="What they said" className="input-field !w-64 !py-1" value={answer.note || ''} onChange={(e) => setAnswer((a) => ({ ...a, note: e.target.value }))} placeholder="What they said" />}
                <button type="button" className="btn-primary btn-sm" onClick={() => status(v, answer.status, answer.date, answer.note)}>Save</button>
                <button type="button" className="btn-ghost btn-sm" onClick={() => setAnswer(null)}>Cancel</button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-1.5">
                {v.status === 'draft' && <button type="button" className="btn-secondary btn-sm" onClick={() => status(v, 'sent', today)}>Sent to them</button>}
                <button type="button" className="btn-secondary btn-sm" onClick={() => setAnswer({ id: v.id, status: 'agreed', date: today })}>They agreed</button>
                <button type="button" className="btn-ghost btn-sm" onClick={() => setAnswer({ id: v.id, status: 'refused', date: today, note: '' })}>They did not agree</button>
              </div>
            ))}
          </div>
        );
      })}
    </Panel>
  );
}
